// ══════════════════════════════════════════════════════════════════════
// Compare — multi-run side-by-side with synced timelines + real
// correlation analysis + portfolio combination diagnostics.
//
// Accepts run IDs via URL (#compare?a=<uuid>&b=<uuid>) and hydrates
// from GQ.state. Falls back to mock data when no runs present.
// ══════════════════════════════════════════════════════════════════════

function ComparePage({ onToast }) {
  const [runs, setRuns] = React.useState({ a: null, b: null });
  const [loading, setLoading] = React.useState(false);

  // Hydrate runs from URL / state on mount
  React.useEffect(() => {
    (async () => {
      setLoading(true);
      const { params } = window.GQ?.state?.parseHash() || { params: {} };
      const aId = params.a || window.GQ?.state?.getLastBacktestRunId();
      const bId = params.b || params.compare;
      const fetchAny = async (id) => {
        if (!id) return null;
        return await window.GQ.state.fetchBacktestRun(id)
            || await window.GQ.state.fetchOptimizationRun(id)
            || await window.GQ.state.fetchStressRun(id)
            || null;
      };
      const a = await fetchAny(aId);
      const b = await fetchAny(bId);
      setRuns({ a, b });
      setLoading(false);
    })();
  }, []);

  function gen(seed, vol) {
    let s = seed, r = () => { s = (s*16807)%2147483647; return (s-1)/2147483646; };
    let v = 100; return Array.from({length:60}, () => { v *= 1 + (r()-.45)*vol; return v; });
  }

  // Demo fallback
  const DEMO = {
    A: { name:"Strategy A", ret:0.574, cagr:0.228, sharpe:1.84, sortino:2.31, maxDD:-0.083, win:0.642, trades:142, eq:gen(7,0.025) },
    B: { name:"Strategy B", ret:0.412, cagr:0.182, sharpe:1.61, sortino:1.94, maxDD:-0.102, win:0.598, trades:178, eq:gen(13,0.028) },
  };

  // Derive comparison data from real runs if available
  const fromRun = (run) => {
    if (!run) return null;
    const m = run.metrics || run.best_metrics || {};
    const eq = (run.equity_curve || []).map(p => p.v ?? p);
    return {
      id: run.id, strategy_id: run.strategy_id,
      name: 'Run ' + (run.id?.slice(0, 8) || '?'),
      ret: m.totalReturn ?? 0,
      cagr: m.cagr ?? 0,
      sharpe: m.sharpe ?? 0,
      sortino: m.sortino ?? 0,
      maxDD: -(m.maxDrawdown ?? 0),
      win: m.winRate ?? 0,
      trades: (run.trades || []).length || m.tradeCount || 0,
      eq: eq.length ? eq : gen(11, 0.02),
    };
  };
  const A = fromRun(runs.a) || DEMO.A;
  const B = fromRun(runs.b) || DEMO.B;

  // Real correlation between the two equity curves (return-based)
  const corr = React.useMemo(() => {
    if (!window.GQ?.stats) return 0;
    const rA = window.GQ.stats.equityToReturns(A.eq);
    const rB = window.GQ.stats.equityToReturns(B.eq);
    const n = Math.min(rA.length, rB.length);
    return window.GQ.stats.correlation(rA.slice(0, n), rB.slice(0, n));
  }, [A.eq, B.eq]);

  // Portfolio blend: 50/50 weighted return series → resulting Sharpe & DD
  const blend = React.useMemo(() => {
    if (!window.GQ?.stats) return null;
    const rA = window.GQ.stats.equityToReturns(A.eq);
    const rB = window.GQ.stats.equityToReturns(B.eq);
    const n = Math.min(rA.length, rB.length);
    const combo = []; for (let i = 0; i < n; i++) combo.push(0.5 * rA[i] + 0.5 * rB[i]);
    const eq = window.GQ.stats.returnsToEquity(combo, 100);
    return {
      sharpe: window.GQ.stats.sharpe(combo),
      maxDD: window.GQ.stats.maxDrawdown(eq).max,
      diversification: 1 - corr,
    };
  }, [A.eq, B.eq, corr]);

  const diff = (x, y) => x - y;
  const diffPct = (x, y) => ((x - y) * 100).toFixed(1);

  const Row = ({ label, va, vb, fmt = "pct", sub = false }) => {
    const valA = fmt==="pct" ? `${va>=0?"+":""}${(va*100).toFixed(1)}%` : (typeof va === 'number' ? va.toFixed(2) : va);
    const valB = fmt==="pct" ? `${vb>=0?"+":""}${(vb*100).toFixed(1)}%` : (typeof vb === 'number' ? vb.toFixed(2) : vb);
    const better = sub ? va < vb : va > vb;
    return (
      <tr style={{ borderBottom:"1px solid var(--border)" }}>
        <td style={{ padding:"10px 8px", fontFamily:"var(--font-ui)", fontSize:12, color:"var(--muted)" }}>{label}</td>
        <td style={{ padding:"10px 8px", fontFamily:"var(--font-mono)", fontSize:13, color: better?"var(--green)":"var(--ink)", fontWeight:better?500:400 }}>{valA}</td>
        <td style={{ padding:"10px 8px", fontFamily:"var(--font-mono)", fontSize:13, color: !better?"var(--green)":"var(--ink)", fontWeight:!better?500:400 }}>{valB}</td>
        <td style={{ padding:"10px 8px", fontFamily:"var(--font-mono)", fontSize:12, color: diff(va,vb)>=0?"var(--green)":"var(--red)" }}>
          {fmt==="pct" ? `${diff(va,vb)>=0?"+":""}${diffPct(va,vb)}pp` : `${diff(va,vb)>=0?"+":""}${diff(va,vb).toFixed(2)}`}
        </td>
      </tr>
    );
  };

  // Sync timelines: normalize both equity curves to same length
  const N = Math.min(A.eq.length, B.eq.length);
  const eqA = A.eq.slice(0, N), eqB = B.eq.slice(0, N);

  return (
    <div style={{ display:"flex", flexDirection:"column", gap:20 }}>
      <PageHdr
        icon={GitMerge} iconColor="#D946EF" iconBg="rgba(217,70,239,0.10)"
        title="Compare runs"
        subtitle={runs.a || runs.b ? "Run-addressable comparison" : "Side-by-side tearsheet diff"}
        action={
          <div style={{display:"flex",gap:6}}>
            {window.PipelineContextChip && <PipelineContextChip page="compare" />}
          </div>
        }
      />

      {loading && (
        <div className="card" style={{display:"flex",alignItems:"center",justifyContent:"center",gap:8,padding:20}}>
          <RefreshCw size={14} color="#3b9eff" style={{animation:"spin 1s linear infinite"}}/>
          <span style={{fontFamily:"var(--font-ui)",fontSize:12,color:"var(--muted)"}}>Loading runs…</span>
        </div>
      )}

      {!loading && !runs.a && !runs.b && (
        <div className="card" style={{ background:"rgba(59,158,255,0.04)", border:"1px solid rgba(59,158,255,0.18)" }}>
          <div style={{display:"flex",alignItems:"flex-start",gap:10}}>
            <Info size={16} color="#3b9eff" style={{flexShrink:0,marginTop:1}}/>
            <div>
              <div style={{ fontFamily:"var(--font-ui)", fontSize:13, fontWeight:600, color:"var(--ink)", marginBottom:4 }}>Showing demo data</div>
              <div style={{ fontFamily:"var(--font-ui)", fontSize:12, color:"var(--body)", lineHeight:1.55 }}>
                No runs in URL. Open <strong>Strategy Detail</strong> for any strategy, click <em>Re-open →</em> on two historical runs, or share a deep-link like
                <code style={{fontFamily:"var(--font-mono)",fontSize:11,color:"var(--ink)",background:"rgba(255,255,255,0.04)",padding:"1px 6px",borderRadius:4,marginLeft:4}}>#compare?a=&lt;uuid&gt;&amp;b=&lt;uuid&gt;</code>.
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Equity overlay (synced) */}
      <div className="card">
        <div className="card-title" style={{ display:"flex", justifyContent:"space-between" }}>
          <span>Equity curves (synced)</span>
          <span style={{ fontFamily:"var(--font-mono)", fontSize:11, color:"var(--muted)", display:"flex", gap:14 }}>
            <span style={{ color:"#11ff99" }}>— {A.name}</span>
            <span style={{ color:"#D946EF" }}>— {B.name}</span>
          </span>
        </div>
        <div style={{ position:"relative", height:160 }}>
          <svg width="100%" viewBox="0 0 600 160" preserveAspectRatio="none" style={{ display:"block" }}>
            {[eqA, eqB].map((eq, i) => {
              const color = i===0 ? "#11ff99" : "#D946EF";
              const min = Math.min(...eq), max = Math.max(...eq);
              const rx = j => (j/(eq.length-1))*600;
              const ry = v => 160 - ((v-min)/(max-min||1))*(160-10) - 5;
              const line = eq.map((v,j)=>`${j===0?"M":"L"}${rx(j)},${ry(v)}`).join(" ");
              return <path key={i} d={line} fill="none" stroke={color} strokeWidth="1.8" strokeLinecap="round"/>;
            })}
          </svg>
        </div>
      </div>

      {/* Correlation + portfolio combination */}
      <div className="card" style={{ background: "rgba(217,70,239,0.04)", border: "1px solid rgba(217,70,239,0.2)" }}>
        <div className="card-title">Strategy diversification</div>
        <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr 1fr", gap: 14, marginTop: 8 }}>
          <div>
            <div style={{ fontFamily:"var(--font-ui)", fontSize:11, color:"var(--muted)" }}>Return correlation</div>
            <div style={{ fontFamily:"var(--font-mono)", fontSize:22, color: Math.abs(corr) > 0.7 ? '#f87171' : '#11ff99' }}>{corr.toFixed(2)}</div>
            <div style={{ fontFamily:"var(--font-ui)", fontSize:10, color:"var(--subtle)" }}>{Math.abs(corr) > 0.7 ? 'High — limited diversification' : Math.abs(corr) > 0.3 ? 'Moderate' : 'Low — good diversification'}</div>
          </div>
          <div>
            <div style={{ fontFamily:"var(--font-ui)", fontSize:11, color:"var(--muted)" }}>50/50 blend Sharpe</div>
            <div style={{ fontFamily:"var(--font-mono)", fontSize:22, color: '#D946EF' }}>{blend ? blend.sharpe.toFixed(2) : '—'}</div>
            <div style={{ fontFamily:"var(--font-ui)", fontSize:10, color:"var(--subtle)" }}>Higher than either alone?</div>
          </div>
          <div>
            <div style={{ fontFamily:"var(--font-ui)", fontSize:11, color:"var(--muted)" }}>Diversification ratio</div>
            <div style={{ fontFamily:"var(--font-mono)", fontSize:22, color: '#3b9eff' }}>{blend ? blend.diversification.toFixed(2) : '—'}</div>
            <div style={{ fontFamily:"var(--font-ui)", fontSize:10, color:"var(--subtle)" }}>(1 − ρ); higher = more diversifying</div>
          </div>
        </div>
      </div>

      {/* Metrics diff table */}
      <div className="card">
        <div className="card-title">Metrics diff</div>
        <table style={{ width:"100%", borderCollapse:"collapse" }}>
          <thead>
            <tr style={{ borderBottom:"1px solid var(--border)" }}>
              <th style={{ fontFamily:"var(--font-ui)", fontSize:10, color:"var(--subtle)", fontWeight:500, textAlign:"left", padding:"4px 8px", paddingBottom:8, letterSpacing:".05em" }}>METRIC</th>
              <th style={{ fontFamily:"var(--font-ui)", fontSize:10, color:"#11ff99", fontWeight:500, textAlign:"left", padding:"4px 8px", paddingBottom:8, letterSpacing:".05em" }}>{A.name.toUpperCase()}</th>
              <th style={{ fontFamily:"var(--font-ui)", fontSize:10, color:"#D946EF", fontWeight:500, textAlign:"left", padding:"4px 8px", paddingBottom:8, letterSpacing:".05em" }}>{B.name.toUpperCase()}</th>
              <th style={{ fontFamily:"var(--font-ui)", fontSize:10, color:"var(--subtle)", fontWeight:500, textAlign:"left", padding:"4px 8px", paddingBottom:8, letterSpacing:".05em" }}>DIFF</th>
            </tr>
          </thead>
          <tbody>
            <Row label="Total return"  va={A.ret}     vb={B.ret}/>
            <Row label="CAGR"          va={A.cagr}    vb={B.cagr}/>
            <Row label="Sharpe ratio"  va={A.sharpe}  vb={B.sharpe} fmt="num"/>
            <Row label="Sortino"       va={A.sortino} vb={B.sortino} fmt="num"/>
            <Row label="Max drawdown"  va={A.maxDD}   vb={B.maxDD} sub={true}/>
            <Row label="Win rate"      va={A.win}     vb={B.win}/>
            <Row label="Trades"        va={A.trades}  vb={B.trades} fmt="num"/>
          </tbody>
        </table>
      </div>
    </div>
  );
}

window.ComparePage = ComparePage;
